import { Table as TableType, flexRender } from '@tanstack/react-table';

import { useAutoFillColumns } from '@/utils/hooks/useAutoFillColumns';
import useLanguage from '@/utils/hooks/useLanguage';
import { useVirtualizer } from '@tanstack/react-virtual';
import cn from 'classnames';
import { Fragment, useMemo, useRef } from 'react';
import { MdArrowDropDown, MdArrowDropUp, MdInfo } from 'react-icons/md';
import Skeleton from 'react-loading-skeleton';
import { KeyedMutator } from 'swr';
import TableColumnFilters from './TableColumnFilters';

export interface PortalTableGeneralProps {
    filters: any;
    setFilters: (filters) => void;
    defaultFilters: any;
    isLoading: boolean;
    mutate: KeyedMutator<any>;
}

export type PortalVirtualizedTableProps<T extends object> = {
    size?: number;
    tableInstance: TableType<T>;
    isLoadingTableContent: boolean;
    tdClassNames?: string;
    rowVirtualizer?: any;
    columnFiltersEnabled?: boolean;
    globalFilterEnabled?: boolean;
    getColStyle?: (column: any) => React.CSSProperties;
    onRowClick?: (row: any) => void;
    onCellContextMenu?: (row: any) => void;
    onRowDoubleClicked?: (row: any) => void;
    onRowClicked?: (row: any) => void;
};

const PortalVirtualizedTable = <T extends object>(props: PortalVirtualizedTableProps<T>) => {
    const { tableInstance, isLoadingTableContent, size = 29 } = props;

    const lang = useLanguage();
    const { rows } = tableInstance.getRowModel();
    const hasRecords = useMemo(() => rows.length > 0, [rows]);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const { containerRef, getColStyle } = useAutoFillColumns(tableInstance);

    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        estimateSize: () => size, //estimate row height for accurate scrollbar dragging
        getScrollElement: () => tableContainerRef.current,
        //measure dynamic row height, except in firefox because it measures table border height incorrectly
        measureElement: (element, _roEntry, instance) => {
            const cachedSize = instance.measurementsCache[instance.indexFromElement(element)].size;
            const rect = element.getBoundingClientRect();
            if (rect.height > 0 && rect.width > 0) {
                return Math.round(rect.height);
            }
            return cachedSize;
        },
        // measureElement:
        //     typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        //         ? (element) => element?.getBoundingClientRect().height
        //         : undefined,
        overscan: 5
    });

    return (
        <div
            ref={containerRef}
            className={cn('border border-brand-neutral-700 bg-brand-background-dark rounded-md overflow-auto')}>
            <div
                className={cn('flex flex-col h-full overflow-auto')}
                ref={tableContainerRef}
                style={{
                    overflow: 'auto', //our scrollable table container
                    position: 'relative' //needed for sticky header
                }}>
                <table className="grid">
                    <TableHeaders {...props} getColStyle={getColStyle} />
                    {isLoadingTableContent ? (
                        <Skeleton className="h-48 w-full !rounded-none !border-0 !border-t" />
                    ) : (
                        <Fragment>
                            {hasRecords && (
                                <TableBody
                                    rowVirtualizer={rowVirtualizer}
                                    {...props}
                                    getColStyle={getColStyle}
                                    rows={rows}
                                />
                            )}
                            {hasRecords && <TableFooter {...props} getColStyle={getColStyle} />}
                        </Fragment>
                    )}
                </table>
                {!hasRecords && !isLoadingTableContent && (
                    <div className="px-2 py-1.5 flex justify-center items-center text-brand-text text-sm min-h-48 border-t border-brand-neutral-700">
                        {lang.blotters.noTableRecords}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PortalVirtualizedTable;

const TableHeaders = (props: PortalVirtualizedTableProps<any>) => {
    const { tableInstance, columnFiltersEnabled, getColStyle } = props;
    const headerGroups = tableInstance.getHeaderGroups();
    const rows = tableInstance.getRowModel().rows;

    return (
        <thead
            className={cn(
                'grid sticky top-0 bg-brand-background-dark border-brand-neutral-700 rounded-t-md py-1 z-[1]',
                {
                    // ['border-b']: !columnFiltersEnabled,
                    // ['border-b-0']: columnFiltersEnabled || rows.length > 0
                }
            )}>
            {headerGroups.map((headerGroup) => (
                <tr key={headerGroup.id} className="flex w-full">
                    {headerGroup.headers.map((header, index) => {
                        const isSorted = header.column.getIsSorted() as string;
                        const columnDef = header.column.columnDef as any;
                        return (
                            <th
                                key={header.id}
                                colSpan={header.colSpan}
                                className={cn('flex relative py-1.5 text-xs font-normal text-brand-text', {
                                    ['pl-2 pr-2']: index === 0,
                                    ['pr-2']: index !== 0 && index !== headerGroup.headers.length - 1,
                                    ['px-2']: index === headerGroup.headers.length - 1
                                })}
                                style={getColStyle?.(header.column)}>
                                {!header.isPlaceholder && (
                                    <div className="flex flex-col gap-1">
                                        <div
                                            className={cn('flex gap-1 items-center pr-3', {
                                                'cursor-pointer select-none': header.column.getCanSort()
                                            })}
                                            onClick={header.column.getToggleSortingHandler()}>
                                            <div className="truncate">
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                            </div>
                                            {columnDef?.toolTip && (
                                                <MdInfo
                                                    title={columnDef.toolTip}
                                                    className="w-4 h-4 text-brand-text cursor-pointer"
                                                />
                                            )}
                                            {isSorted && (
                                                <div className="h-4 w-4">
                                                    {isSorted === 'asc' && (
                                                        <MdArrowDropUp className="h-4 w-4 text-primary" />
                                                    )}
                                                    {isSorted === 'desc' && (
                                                        <MdArrowDropDown className="h-4 w-4 text-primary" />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div
                                            onMouseDown={header.getResizeHandler()}
                                            onTouchStart={header.getResizeHandler()}
                                            className={cn('table-resizer', {
                                                isResizing: header.column.getIsResizing()
                                            })}
                                        />
                                    </div>
                                )}
                            </th>
                        );
                    })}
                </tr>
            ))}
            {columnFiltersEnabled && <TableColumnFilters headerGroups={headerGroups} {...props} />}
        </thead>
    );
};

const TableBody = (props: any) => {
    const { rows, tdClassNames, rowVirtualizer, getColStyle, onCellContextMenu, onRowDoubleClicked, onRowClicked } =
        props;

    return (
        <tbody
            // border-b border-brand-neutral-700
            className="grid relative w-full rounded-md min-h-48"
            style={{
                height: `${rowVirtualizer.getTotalSize()}px`
            }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow, index) => {
                const row = rows[virtualRow.index];

                return (
                    <tr
                        key={row.id}
                        data-index={index}
                        ref={(node) => rowVirtualizer.measureElement(node)}
                        onClick={(e) => {
                            row.getToggleExpandedHandler()();
                            onRowClicked?.({ ...row, event: e });
                        }}
                        onDoubleClick={(e) => onRowDoubleClicked?.({ ...row, event: e })}
                        onContextMenu={(e) => onCellContextMenu?.({ ...row, event: e })}
                        className={cn('border-brand-neutral-700 hover:bg-brand-neutral-700 cursor-pointer', {
                            'bg-brand-background-dark': index % 2 !== 0
                        })}
                        style={{
                            display: 'flex',
                            position: 'absolute',
                            transform: `translateY(${virtualRow.start}px)`, //this should always be a `style` as it changes on scroll
                            width: '100%'
                        }}>
                        {row.getVisibleCells().map((cell, index) => (
                            <td
                                key={cell.id}
                                className={cn(
                                    'py-2 border-t border-brand-neutral-700 text-xs font-normal lg:text-sm text-brand-text',
                                    {
                                        ['pl-2']: index === 0,
                                        ['pr-2']: index !== 0 && index !== row.getVisibleCells().length - 1,
                                        ['px-2']: index === row.getVisibleCells().length - 1
                                    },
                                    tdClassNames
                                )}
                                style={getColStyle(cell.column)}>
                                <div className="truncate">
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </div>
                            </td>
                        ))}
                    </tr>
                );
            })}
        </tbody>
    );
};

const TableFooter = (props: PortalVirtualizedTableProps<any>) => {
    const { tableInstance, tdClassNames, getColStyle } = props;
    const footerGroups = tableInstance.getFooterGroups();

    return (
        <tfoot className="bg-brand-background-dark rounded-b-md">
            {footerGroups.map((footerGroup) => {
                return (
                    <tr key={footerGroup.id} className="table items-center w-full">
                        {footerGroup.headers.map((footer, index) => {
                            const footerHeader = flexRender(footer.column.columnDef.footer, footer.getContext());
                            return footerHeader ? (
                                <td
                                    key={footer.id}
                                    className={cn(
                                        'py-2 border-t border-brand-neutral-700 text-xs font-normal lg:text-sm text-brand-text',
                                        tdClassNames
                                    )}
                                    style={getColStyle?.(footer.column)}>
                                    {footerHeader}
                                </td>
                            ) : null;
                        })}
                    </tr>
                );
            })}
        </tfoot>
    );
};

// {footerGroup.headers.map((header) => (
//     <Fragment key={header.id}>
//         {flexRender(
//             header.column.columnDef.footer,
//             header.getContext()
//         )}
//     </Fragment>
// ))}
